import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import {
  Home,
  ClipboardList,
  MapPin,
  Play,
  AlertCircle,
  Users,
  LogOut,
  Menu,
  X,
  Factory,
  QrCode,
  Gauge,
  Shield,
  Settings,
  Crown,
  Bell,
  CalendarDays,
  BarChart3,
  History,
  BookOpen,
  Eye,
  Plug
} from 'lucide-react';
import { Button } from './ui/button';
import LanguageSelector from './LanguageSelector';
import NotificationBell from './NotificationBell';
import OfflineIndicator from './OfflineIndicator';

import { API_BASE } from '../utils/apiClient';

const Layout = ({ children }) => {
  const { user, logout, isAdmin, isManager, isSuperAdmin, isDemoReadonly } = useAuth();
  const { tenantName, tenantLogo, primaryColor, accentColor } = useTenant();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Build navigation items based on user role
  const getNavItems = () => {
    const items = [
      { path: '/', label: t('nav.dashboard'), icon: Home },
    ];
    
    // Sites - Admin only (supervisors shouldn't manage sites)
    if (isAdmin()) {
      items.push({ path: '/sites', label: t('nav.sites'), icon: Factory });
    }
    
    // Checklists - All users can view
    items.push({ path: '/checklists', label: t('nav.checklists'), icon: ClipboardList });
    
    // Core features for all users
    items.push({ path: '/gemba', label: t('nav.tour'), icon: Play });
    items.push({ path: '/scan', label: t('nav.scanner'), icon: QrCode });
    items.push({ path: '/actions', label: t('nav.actions'), icon: AlertCircle });
    items.push({ path: '/performance', label: t('nav.performance'), icon: Gauge });
    items.push({ path: '/safety', label: t('nav.safety'), icon: Shield });
    items.push({ path: '/history', label: 'Historique', icon: CalendarDays });
    
    // Analysis - Manager and Admin only
    if (isManager() || isAdmin()) {
      items.push({ path: '/analysis', label: 'Analyse', icon: BarChart3 });
    }
    
    // Admin-only items
    if (isAdmin()) {
      items.push({ path: '/users', label: t('nav.users'), icon: Users });
      items.push({ path: '/settings', label: t('nav.settings'), icon: Settings });
      items.push({ path: '/audit-log', label: t('nav.auditLog'), icon: History });
      items.push({ path: '/documentation', label: 'Documentation', icon: BookOpen });
    }
    
    // Super admin item
    if (isSuperAdmin()) {
      items.push({ path: '/super-admin', label: 'Super Admin', icon: Crown, superAdmin: true });
      items.push({ path: '/admin/diagnostics', label: 'Diagnostic', icon: Shield, superAdmin: true });
      items.push({ path: '/admin/integrations', label: 'Intégrations', icon: Plug, superAdmin: true });
    }
    
    return items;
  };
  
  const navItems = getNavItems();

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="sidebar flex flex-col" style={{ backgroundColor: primaryColor }}>
        {/* Header - Fixed */}
        <div className="p-4 border-b border-[#0A2850] flex-shrink-0" style={{ backgroundColor: primaryColor }}>
          <div className="flex items-center gap-3">
            {tenantLogo ? (
              <img 
                src={tenantLogo} 
                alt="Logo" 
                className="w-12 h-12 object-contain bg-white rounded-lg p-1"
              />
            ) : (
              <img 
                src="/logo.png" 
                alt="Logo" 
                className="w-12 h-12 object-contain bg-white rounded-lg p-1"
              />
            )}
            <div>
              <h1 className="font-bold text-white text-xl font-['Barlow_Condensed'] tracking-wider">
                {tenantName || t('app.name')}
              </h1>
              <p className="text-xs font-medium" style={{ color: accentColor }}>{t('app.tagline')}</p>
            </div>
          </div>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 py-4 overflow-y-auto scrollbar-thin scrollbar-thumb-[#0A2850] scrollbar-track-transparent">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Footer - Fixed at bottom */}
        <div className="p-4 border-t border-[#0A2850] flex-shrink-0" style={{ backgroundColor: primaryColor }}>
          {/* Notification Bell + Language */}
          <div className="flex items-center justify-between mb-4">
            <NotificationBell variant="sidebar" />
            <LanguageSelector variant="minimal" className="flex-1 ml-2 justify-center" />
          </div>
          
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#B98D32] rounded-full flex items-center justify-center">
              <span className="text-sm font-bold text-white">{user?.name?.charAt(0)?.toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
              <p className="text-xs text-[#B98D32] uppercase font-medium">{user?.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-400 hover:text-white hover:bg-[#0A2850]"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('common.logout')}
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header 
        className="md:hidden sticky top-0 z-30 px-4 h-16 flex items-center justify-between"
        style={{ backgroundColor: primaryColor, borderBottom: `4px solid ${accentColor}` }}
      >
        <div className="flex items-center gap-3">
          {tenantLogo ? (
            <img 
              src={tenantLogo} 
              alt="Logo" 
              className="w-10 h-10 object-contain bg-white rounded-lg p-1"
            />
          ) : (
            <img 
              src="/logo.png" 
              alt="Logo" 
              className="w-10 h-10 object-contain bg-white rounded-lg p-1"
            />
          )}
          <span className="font-bold text-white text-lg font-['Barlow_Condensed'] tracking-wider">
            {tenantName || t('app.name')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Notification Bell Component */}
          <NotificationBell />
          <LanguageSelector variant="compact" className="text-white" />
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-white"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/60" onClick={() => setMobileMenuOpen(false)}>
          <div
            className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Menu Header - fixed */}
            <div className="p-4 border-b bg-[#0E346E] flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#B98D32] rounded-full flex items-center justify-center">
                  <span className="font-bold text-white text-lg">{user?.name?.charAt(0)?.toUpperCase()}</span>
                </div>
                <div>
                  <p className="font-semibold text-white">{user?.name}</p>
                  <p className="text-sm text-[#B98D32] uppercase font-medium">{user?.role}</p>
                </div>
              </div>
            </div>
            {/* Nav items - scrollable */}
            <nav className="flex-1 py-2 overflow-y-auto overscroll-contain -webkit-overflow-scrolling-touch">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-4 text-base ${isActive(item.path) ? 'text-[#0E346E] bg-blue-50 border-l-4 border-[#0E346E]' : 'text-gray-700 hover:bg-gray-50'}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            {/* Logout - fixed at bottom */}
            <div className="p-4 border-t flex-shrink-0">
              <Button
                variant="outline"
                className="w-full h-12 border-2"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('common.logout')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="md:ml-64 min-h-screen pb-24 md:pb-0 bg-gray-100">
        {isDemoReadonly() && (
          <div 
            className="sticky top-0 z-20 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-amber-900 bg-amber-100 border-b border-amber-200"
            data-testid="demo-readonly-banner"
          >
            <Eye className="w-4 h-4 flex-shrink-0" />
            <span>Mode Démonstration — Lecture seule. Les modifications ne sont pas autorisées.</span>
          </div>
        )}
        {children}
      </main>
      
      {/* Offline Mode Indicator */}
      <OfflineIndicator />

      {/* Mobile Bottom Navigation - Industrial */}
      <nav className="mobile-nav">
        {navItems.slice(0, 5).map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`mobile-nav-item ${isActive(item.path) ? 'active' : ''}`}
              data-testid={`mobile-nav-${item.path.replace('/', '') || 'dashboard'}`}
            >
              <Icon className={`w-6 h-6 ${isActive(item.path) ? 'text-[#B98D32]' : ''}`} />
              <span className="text-xs mt-1 font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default Layout;
